import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { DrawerPage } from 'components/DrawerPage';
import { NftCardPayment } from 'components/Shared/NftCardPayment';
import { PaymentStatus } from 'components/Shared/NftPaymentStatus';
import { NftPriceSummary } from 'components/Shared/NftPriceSummary';
import { useHistory } from 'hooks/generic/useHistory';
import { useSearchParams } from 'hooks/useSearchParams';
import * as Styled from './styles';
import { useAppDispatch } from 'appRedux/hooks';
import { getNftById } from 'appRedux/restApi';
import { setSession } from 'services/ApiClient';
import { getPath } from 'utils';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import { NFT_CHECKOUT_PATTERN } from 'constants/app/route-reserved-words';
import { STRIPE_KEY } from 'constants/payment/stripe-keys';

const stripePromise = loadStripe(STRIPE_KEY);

export const VeniceCheckout = () => {
  const { pathname } = useLocation();
  const searchParams = useSearchParams();
  const { push } = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState();
  const [isPaymentPending, setIsPaymentPending] = useState<boolean>();
  const [isPaymentSuccess, setIsPaymentSuccess] = useState<boolean>(false);

  const showNFTView = useMemo(
    () => NFT_CHECKOUT_PATTERN.test(pathname),
    [pathname]
  );

  const nftId = useMemo(() => {
    const [, _id] = pathname.match(NFT_CHECKOUT_PATTERN) || [null, null];
    return _id;
  }, [pathname]);

  useEffect(() => {
    if (nftId) {
      dispatch(getNftById(nftId));
    }
  }, [nftId]);

  const handlePaymentSuccess = useCallback(() => {
    if (searchParams.redirectToHome) {
      window.location.href = getPath('/');
    } else {
      push(EAccountRouteKeys.ACCOUNT_PROFILE_MY_NFTS_OWNED);
    }
  }, []);

  useEffect(() => {
    if (searchParams.token) {
      setSession(searchParams.token);
    }
  }, [searchParams.token]);

  useEffect(() => {
    const lsIsPaymentPending = JSON.parse(
      `${localStorage.getItem('paymentPending') || 'null'}`
    );
    setIsPaymentPending(lsIsPaymentPending);
  }, []);

  useEffect(() => {
    if (isPaymentPending) {
      const paymentIntent = JSON.parse(
        `${localStorage.getItem('paymentIntent') || 'null'}`
      );
      setClientSecret(paymentIntent.client_secret);
    }
  }, [isPaymentPending]);

  return (
    <DrawerPage
      open={showNFTView}
      closable={false}
      headerStyle={{
        zIndex: 1,
        position: 'fixed',
        top: 0,
        right: 0,
        background: 'transparent',
        padding: '20px 20px 0'
      }}
      bodyStyle={{
        overflow: 'auto'
      }}
    >
      <Suspense>
        <Styled.Wrapper>
          <Styled.Title>{t('nft.finalCheckout')}</Styled.Title>
          <Styled.PriceWrapper>
            <NftPriceSummary useCurrentNft />
          </Styled.PriceWrapper>
          <Styled.StripeWrapper>
            {clientSecret && (isPaymentPending || isPaymentSuccess) ? (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentStatus
                  handleSetSuccessStep={() => handlePaymentSuccess()}
                />
              </Elements>
            ) : nftId ? (
              <NftCardPayment
                id={Number(nftId)}
                url_key={`/nft-checkout/${nftId}`}
                externalHandleSuccess={() => {
                  setIsPaymentSuccess(true);
                }}
                isFullUrl
              />
            ) : null}
          </Styled.StripeWrapper>
        </Styled.Wrapper>
      </Suspense>
    </DrawerPage>
  );
};
